import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, switchMap, debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { find, map, forEach, uniq, split, toSafeInteger } from 'lodash';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { navigation } from 'app/navigation/navigation';
import { AuthenticationService, DropdownsService, UserService } from 'app/services';
import { environment } from 'environments/environment';
import { AppProviders, GlobalFuntions } from 'app/_helpers';
import { ngxCsv } from 'ngx-csv/ngx-csv';
import { TranslationsService } from 'app/services/translations.service';
import { MailBoxService } from 'app/services/mail-box.service';
import { AttachmentsService } from 'app/services/attachments.service';
import { Router, ActivatedRoute } from '@angular/router';
import { DateAdapter } from '@angular/material/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { QuickAddComponent } from 'app/main/master-data/accounts/quick-add/quick-add.component';
import { MatDialog } from '@angular/material/dialog';
import { QuickAddOwnersComponent } from 'app/main/master-data/owners/quick-add-owners/quick-add-owners.component';
import { QuickCreateComponent } from 'app/main/properties/quick-create/quick-create.component';
import { CommercialsQuickCreateComponent } from 'app/main/commercials/commercials-quick-create/commercials-quick-create.component';
import { MeetingCreateModalComponent } from 'app/main/activities/meetings/meeting-create-modal/meeting-create-modal.component';
import { TasksCreateModalComponent } from 'app/main/activities/tasks/tasks-create-modal/tasks-create-modal.component';
import { ViewingToursModalComponent } from 'app/main/activities/viewingTours/viewing-tours-modal/viewing-tours-modal.component';
import { FaqsService } from 'app/services/faqs.service';
import { ScreenHelpComponent } from 'app/components/screen-help/screen-help.component';
import { GenerateRegisterTokenComponent } from '../generate-register-token/generate-register-token.component';
import { WebsocketService } from 'app/services/websocket.service';
import { CommonService } from 'app/services/common/common.service';
import { WalletService } from 'app/services';

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class ToolbarComponent implements OnInit, OnDestroy {
    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    languages: any;
    navigation: any;
    selectedLanguage: any;
    currentUser: any;
    currentAgency: any;
    env: any;
    subscription_paid: boolean = true;

    // Private
    private _unsubscribeAll: Subject<void> = new Subject<void>();
    userMailBox: any;
    searchValue$ = new Subject<String>();
    img_src: string;
    public unReadCount: string | number = 0;
    auditorRoles: Array<string> = [];
    walletBalanceCents: number = 0;
    walletBalanceLoaded: boolean = false;

    get walletBalanceColor(): string {
        const eur = this.walletBalanceCents / 100;
        if (eur > 49.99) return '#4caf50';
        if (eur >= 25) return '#ff9800';
        return '#f44336';
    }

    get walletBalanceEur(): string {
        return (this.walletBalanceCents / 100).toFixed(2);
    }

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {TranslateService} _translateService
     */
    constructor(
        private _matDialog: MatDialog,
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private _translationsService: TranslationsService,
        private _authenticationService: AuthenticationService,
        private _appProviders: AppProviders,
        private _adapter: DateAdapter<any>,
        private _mailBoxService: MailBoxService,
        private _snackBar: MatSnackBar,
        private router: Router,
        private _faqsService: FaqsService,
        private _userService: UserService,
        private _attachmentsService: AttachmentsService,
        private socket: WebsocketService,
        public _globalFuntions: GlobalFuntions,
        private _commonService: CommonService,
        private _walletService: WalletService,
    ) {
        this.auditorRoles = this._commonService.getAuditorRoles();
        this.languages = this._appProviders.siteLanguages;

        this.navigation = navigation;

        this.currentUser = this._authenticationService.currentUserValue;
        if (this.currentUser && this.currentUser.default_lang && !localStorage.getItem('lang')) {
            const appLang = this.currentUser.default_lang === 'es' ? 'es_AR' : this.currentUser.default_lang;
            this._translateService.use(appLang);
            this._adapter.setLocale(appLang);
        }
        this.currentAgency = this._authenticationService.currentAgencyValue;

        if (this.currentUser?.user_role !== 'admin' && this.currentAgency?.subscription_paid !== undefined && !this.currentAgency?.subscription_paid) {
            this.subscription_paid = false;
        }
        this.env = environment;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to the config changes
        this._userService.profileImageUrl$.subscribe(imageUrl => {
            if(imageUrl) {
                this.currentUser.user_dp = imageUrl;
                this.getUserProfileImage();
            }
        });
        this.socket.listen('NotificationCount')
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(res => {
                this.unReadCount = res;
                if(res > 99){
                    this.unReadCount = "99+"
                }
        });

        if (this.currentAgency?._id) {
            this._walletService.getWalletDetails(this.currentAgency._id)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (res) => {
                        this.walletBalanceCents = res?.balance ?? 0;
                        this.walletBalanceLoaded = true;
                    },
                    error: () => { this.walletBalanceLoaded = true; }
                });
        }

        this.socket.listen('WalletBalance')
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((balance: any) => {
                if (typeof balance === 'number') {
                    this.walletBalanceCents = balance;
                    this.walletBalanceLoaded = true;
                }
            });
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((settings) => {
                this.horizontalNavbar = settings.layout.navbar.position === 'top';
                this.rightNavbar = settings.layout.navbar.position === 'right';
                this.hiddenNavbar = settings.layout.navbar.hidden === true;
            });

        if (this.currentUser && this.currentUser.default_lang && !localStorage.getItem('lang')) {
            const appLang = this.currentUser.default_lang === 'es' ? 'es_AR' : this.currentUser.default_lang;
            this.selectedLanguage = find(this.languages, { id: appLang });
        } else {
            this.selectedLanguage = find(this.languages, { id: this._translateService.currentLang });
        }

        this.searchValue$.pipe(
            debounceTime(300),
            switchMap((search: string) => {
                return this._userService.searchData(search);
            }),
        ).subscribe((data: any) => {
            this._fuseSidebarService.getSidebar('searchPanel').open()
        });
        this.getUserProfileImage();

    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    /**
     * Search
     *
     * @param value
     */
    search(value: any): void {
        // Do your search here...
        if (typeof value === 'string') {
            this._userService.searchData(value)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((data) => {
                    this._fuseSidebarService.getSidebar('searchPanel').open();
                });
        }
    }

    /**
     * Set the language
     *
     * @param lang
     */
    setLanguage(lang): void {
        localStorage.setItem('lang', lang.id);

        // Set the selected language for the toolbar
        this.selectedLanguage = lang;

        // Use the selected language for translations
        this._translateService.use(lang.id);

        // for date picker
        this._adapter.setLocale(lang.id);

        // Update the <html> tag’s lang attribute for external systems (like chatbot..) to detect current language
        if (typeof document !== 'undefined' && document.documentElement) {
            document.documentElement.lang = lang.id;
        }
    }

    logout(): void {
        this._authenticationService.logout();
    }

    downloadCsv(): void {
        const missingTranslations = JSON.parse(localStorage.getItem('missingTranslations'));
        this._translationsService.getMissing(missingTranslations)
            .subscribe((mt) => {
                const langs = this._translateService.getLangs();

                const data = map(mt, (translations, key) => {
                    const mapData = {} as any;
                    mapData.key = key;
                    forEach(langs, (lang) => {
                        const translation = find(translations, (t) => t.language === lang);
                        mapData[lang] = '';
                        if (translation) {
                            mapData[lang] = translation.value;
                        }
                    });
                    return mapData;
                });

                const options = {
                    showLabels: true,
                    headers: ['key', ...langs],
                };
                // tslint:disable-next-line: no-unused-expression
                new ngxCsv(data, 'missingTranslations', options);
            });

    }

    getUrl(): void {
        const paramsArr = split(this.router.url, '/');
        const arr = [];

        forEach(paramsArr, (value) => {
            if (!this._globalFuntions.isValidMongoId(value) && !toSafeInteger(value)) {
                arr.push(value);
            }
        });

        const total = arr.length;

        let url = '/';
        for (let i = 1; i < (total); i++) {
            if (i !== total - 1) {
                url += arr[i] + '/';
            } else {
                url += arr[i];
            }
        }
        this._faqsService.getByUrl(url).subscribe((data: any) => {
            if (data) {
                const dialogRef = this._matDialog.open(ScreenHelpComponent, {
                    panelClass: 'help-screen-dialog',
                    disableClose: false,
                    data: data.content
                });
                dialogRef.afterClosed()
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe(result => {
                        if (result) {
                        }
                    });
            } else {
                this._snackBar.open(this._translateService.instant('No FAQs'), this._translateService.instant('Close'), {
                    duration: 2000,
                });
            }

        });
    }

    mailBox(): void {
        if (this.currentUser.user_id) {
            this._mailBoxService
                .getSettings()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((data) => {
                    this.userMailBox = data;
                    if (this.userMailBox && this.userMailBox.username) {
                        this.router.navigate(['/dashboard/mail/inbox']);
                    } else {
                        this._snackBar.open(
                            this._translateService.instant(
                                'No email connected!'
                            ),
                            this._translateService.instant('Close'),
                            {
                                duration: 2000,
                            }
                        );
                    }
                });
        }
    }

    openAccountsDialog(): void {
        const dialogRef = this._matDialog.open(QuickAddComponent, {
            panelClass: 'event-form-dialog',
            disableClose: false
        });
        dialogRef.afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(result => {
                if (result) {

                }
            });
    }

    openOwnersDialog(): void {
        const dialogRef = this._matDialog.open(QuickAddOwnersComponent, {
            panelClass: 'event-form-dialog',
            disableClose: false
        });
        dialogRef.afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(result => {
                if (result) {
                }
            });
    }

    quickAddProperty(): void {
        const searchDialogRef = this._matDialog.open(QuickCreateComponent, {
            panelClass: 'event-form-dialog',
        });
        searchDialogRef.afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response: any) => {
                // if (response) {
                //     this.filter = _.pickBy(response.value, _.identity);
                //     this.value.setValue(0);
                // }
            });
    }

    quickAddCommercial(): void {
        const searchDialogRef = this._matDialog.open(CommercialsQuickCreateComponent, {
            panelClass: 'event-form-dialog',
        });
        searchDialogRef.afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response: any) => {
            });
    }

    addActivities(activity: any): void {
        let dialogRef: any;
        if (activity === 'Meeting') {
            dialogRef = this._matDialog.open(MeetingCreateModalComponent, {
                panelClass: 'event-form-dialog',
                data: {}
            });
        } else if (activity === 'Task') {
            dialogRef = this._matDialog.open(TasksCreateModalComponent, {
                panelClass: 'event-form-dialog',
                data: {}
            });
        } else if (activity === 'Viewing') {
            dialogRef = this._matDialog.open(ViewingToursModalComponent, {
                panelClass: 'event-form-dialog',
                data: {}
            });
        }

        dialogRef.afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response: any) => {
                if (response) {

                }
            });

    }

    openTokenDialog() {
        const ref = this._matDialog.open(GenerateRegisterTokenComponent,{
            panelClass:'register-token'
        });
        // ref.
    }

    getUserProfileImage() {
        if (this.currentUser && this.currentUser?.user_id && this.currentUser?.user_dp) {
            this.img_src = this._attachmentsService.getImageURL("users", this.currentUser?.user_id, this.currentUser?.user_dp);
        }
    }

}
